import { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "@jsq/ds/core";
import { client } from "~/apollo/client";
import { globalEnv } from "~/global-env";
import { ComplianceObjectType } from "~/graphql/supergraphEnums";
import {
  useCreateCaseForIndividualInvestorMutation,
  useCreateCaseForInstitutionInvestorMutation,
  useCreateCaseForJointInvestorsMutation,
  useCreateComplianceCaseFromProfileMutation,
  useGetProgramNamesAndIdsQuery,
} from "~/graphql/supergraphHooks";
import { useInvestorWizard } from "../context";
import {
  buildExternalObjects,
  buildIndividualInput,
  buildInstitutionInput,
  buildJointInput,
  resolveCaseType,
} from "./utils";

export const useCreateCase = () => {
  const history = useHistory();
  const { caseInfo, newProfileData, selectedProfileGuid } = useInvestorWizard();
  const [submitting, setSubmitting] = useState(false);

  const programsResult = useGetProgramNamesAndIdsQuery({ fetchPolicy: "cache-and-network" });
  const programNameToId = useMemo(() => {
    const entries =
      programsResult.data?.programsAssignableToEntities?.map((item): [string, number] => [
        item.name,
        item.id,
      ]) ?? [];
    return Object.fromEntries(entries);
  }, [programsResult.data]);

  const mutationOptions = useMemo(
    () => ({
      onError: (e: Error) => {
        toast.error(e.message);
      },
      onCompleted: async () => {
        await client.refetchQueries({ include: "active" });
      },
    }),
    [],
  );

  const [createIndividualCase] = useCreateCaseForIndividualInvestorMutation(mutationOptions);
  const [createInstitutionCase] = useCreateCaseForInstitutionInvestorMutation(mutationOptions);
  const [createJointCase] = useCreateCaseForJointInvestorsMutation(mutationOptions);
  const [createCaseFromProfile] = useCreateComplianceCaseFromProfileMutation(mutationOptions);

  const goToCase = useCallback(
    (caseId?: number) => {
      if (!caseId) return;
      toast.success("New case created");
      history.push(`${globalEnv.complianceRoute}/case/${caseId}`);
    },
    [history],
  );

  const handleCreateCase = useCallback(async () => {
    if (!caseInfo || (!newProfileData && !selectedProfileGuid)) return;

    const programId = caseInfo.programName ? programNameToId[caseInfo.programName] : undefined;
    if (!programId) {
      toast.error(
        `Case cannot be created because the entity ${caseInfo.entityName ?? ""} does not have a compliance program set`,
      );
      return;
    }

    const { externalArena, externalEntity, caseExternalObject } =
      buildExternalObjects(caseInfo);
    const caseType = resolveCaseType(caseExternalObject);

    setSubmitting(true);
    try {
      if (selectedProfileGuid) {
        const response = await createCaseFromProfile({
          variables: {
            caseInput: {
              profileGuid: selectedProfileGuid,
              caseType,
              assignSelf: caseInfo.assignToMyself,
              programId,
              externalEntity,
              externalArena,
              caseExternalObject,
            },
          },
        });
        goToCase(response.data?.createComplianceCaseFromProfile.id);
        return;
      }

      if (!newProfileData) return;
      const documentIds = newProfileData.documentId ? [newProfileData.documentId] : null;
      const sharedVariables = {
        programId,
        externalArena,
        externalEntity,
        caseExternalObject,
        caseType,
        assignSelf: caseInfo.assignToMyself,
        documentIds,
      };

      if (newProfileData.category === ComplianceObjectType.Individual) {
        const response = await createIndividualCase({
          variables: { profileData: buildIndividualInput(newProfileData), ...sharedVariables },
        });
        goToCase(response.data?.createCaseWithNewIndividualProfile.case.id);
      } else if (newProfileData.category === ComplianceObjectType.Joint) {
        const response = await createJointCase({
          variables: { profileData: buildJointInput(newProfileData), ...sharedVariables },
        });
        goToCase(response.data?.createCaseWithNewJointProfile.case.id);
      } else {
        const response = await createInstitutionCase({
          variables: { profileData: buildInstitutionInput(newProfileData), ...sharedVariables },
        });
        goToCase(response.data?.createCaseWithNewInstitutionProfile.case.id);
      }
    } catch {
      // Mutation errors are surfaced via the shared onError handler; caught here only to
      // prevent an unhandled rejection from the awaited mutation.
    } finally {
      setSubmitting(false);
    }
  }, [
    caseInfo,
    newProfileData,
    selectedProfileGuid,
    programNameToId,
    createIndividualCase,
    createInstitutionCase,
    createJointCase,
    createCaseFromProfile,
    goToCase,
  ]);

  return {
    handleCreateCase,
    submitting,
    programsLoading: programsResult.loading,
  };
};
